import { BASE_ALERTS_URL, BASE_URL } from './api'
import { ZELLO_BASE_URL } from './zello'

/**
 * How a target gets probed. The kind is chosen per target to be the most
 * faithful test available, not for uniformity:
 *
 * - `http`    a normal CORS request. Only our own hosts send CORS headers, so
 *             only they can be probed this way — but any HTTP status coming
 *             back proves the request crossed the network.
 * - `no-cors` an opaque request. Proves DNS, TCP 443 and TLS to the host
 *             succeeded, which is what a firewall allowlist actually governs.
 *             Cannot read the status, so a proxy that answers with a block
 *             page reads as reachable.
 * - `ws`      a real WebSocket handshake. The highest-fidelity check, and the
 *             one that matters most for allowlisting, since the upgrade is
 *             often blocked while 443 is open. Only usable where we can build
 *             a URL the server will actually accept.
 */
export type ProbeKind = 'http' | 'no-cors' | 'ws'

/**
 * When a target gets probed.
 *
 * - `core`  every load, before sign-in. These hosts are needed by every
 *           property, and one of them being blocked is why signing in or
 *           closing an alert fails.
 * - `voice` only when the selected org has voice enabled *and* the signed-in
 *           user holds a voice license for it. Probing zellowork.io for a
 *           property that does not use push-to-talk, or for a user who never
 *           connects to it (a super admin viewing channels, anyone unlicensed),
 *           would report a problem they do not have — plenty of networks block
 *           it correctly.
 */
export type ConnectivityGroup = 'core' | 'voice'

export interface ConnectivityTarget {
  /** Stable key, used for translation lookups and as a React key. */
  id: string
  /** Human label for the checklist row. */
  label: string
  url: string
  kind: ProbeKind
  /**
   * What stops working when this host is blocked. This is the line an IT team
   * reads, so it names the symptom rather than the component.
   */
  impact: string
  group: ConnectivityGroup
}

// Read the same way `./index` reads them. Importing from there instead would
// make this module and its own barrel file circular.
const PUSHER_KEY = window._env_?.PUSHER_APP_KEY || process.env?.PUSHER_APP_KEY || ''
const PUSHER_CLUSTER = window._env_?.PUSHER_APP_CLUSTER || process.env?.PUSHER_APP_CLUSTER || ''

// Pusher's own client sends these query params on the handshake; without them
// the server rejects the upgrade and a reachable host would look blocked.
const PUSHER_WS_URL = `wss://ws-${PUSHER_CLUSTER}.pusher.com/app/${PUSHER_KEY}?protocol=7&client=js&version=8.4.0`

// Stand-in subdomain for `?connectivity-preview`, which renders the panel
// without probing anything and so has no org to read one from.
const PREVIEW_SUBDOMAIN = 'your-property'

export const CORE_TARGETS: ConnectivityTarget[] = [
  {
    id: 'main_api',
    label: 'React Mobile API',
    url: `${BASE_URL}healthcheck`,
    kind: 'http',
    impact: 'Signing in, and loading properties, users, and devices.',
    group: 'core',
  },
  {
    id: 'alert_api',
    label: 'React Mobile Alert API',
    url: `${BASE_ALERTS_URL}healthcheck`,
    kind: 'http',
    impact: 'Receiving alerts, and closing or resolving them.',
    group: 'core',
  },
  {
    id: 'pusher',
    label: 'Pusher realtime',
    url: PUSHER_WS_URL,
    kind: 'ws',
    impact: 'Live alert updates. Without it the dashboard will not update on its own.',
    group: 'core',
  },
]

/**
 * The Zello target, at the exact URL `VoiceProvider` opens.
 *
 * Probed as a real `wss://` handshake and nothing else. Until September 2026
 * this was a `no-cors` GET of the apex `https://zellowork.io/`, chosen because
 * the per-property subdomain was believed to be unavailable at probe time. Two
 * things were wrong with that. The subdomain *is* available — the voice group
 * only runs once an org is selected, and `selectedOrg.zelloSubdomain` is the
 * same field the transport builds its socket from. And the apex is not the
 * service: it stopped answering HTTP on both 443 and 80 while `/ws/{subdomain}`
 * kept returning `101 Switching Protocols`, so every voice-enabled property
 * started reporting a firewall problem it did not have.
 *
 * A handshake is also the higher-fidelity test regardless: the upgrade is
 * frequently blocked on networks where plain 443 is open, which is exactly the
 * case an allowlist misses.
 */
export const voiceTargets = (subdomain?: string): ConnectivityTarget[] =>
  subdomain
    ? [
        {
          id: 'zello',
          label: 'Zello voice',
          url: `${ZELLO_BASE_URL}${subdomain}`,
          kind: 'ws',
          impact: 'Push-to-talk voice for this property.',
          group: 'voice',
        },
      ]
    : []

/** Every target, for the preview and for id/group lookups. */
export const CONNECTIVITY_TARGETS: ConnectivityTarget[] = [...CORE_TARGETS, ...voiceTargets(PREVIEW_SUBDOMAIN)]

/** Per-probe ceiling. Long enough for a slow link, short enough to not stall load. */
export const CONNECTIVITY_TIMEOUT_MS = 8000

/**
 * How long the voice row waits for the app's own Zello socket to settle before
 * falling back to probing the URL itself. Comfortably past the transport's own
 * 8s logon timeout, so a slow-but-working logon answers the question first.
 */
export const VOICE_VERDICT_GRACE_MS = 12000

/** Set once the operator dismisses the overlay, so it does not nag all session. */
export const CONNECTIVITY_DISMISSED_KEY = 'connectivity_check_dismissed'
